import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { LoadingState, ErrorState } from "@/components/LoadingError";
import { apiFetch } from "@/utils/api";

export function PartHistoryPage() {
  const { systemId, partId } = useParams();
  const [data, setData] = useState<{
    system: { name: string };
    part: Record<string, unknown>;
    onHand: number;
    transactions: Record<string, unknown>[];
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!systemId || !partId) return;
    (async () => {
      try {
        const result = await apiFetch<typeof data>(`/systems/${systemId}/parts/${partId}/history`);
        setData(result);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, [systemId, partId]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (!data) return null;

  const part = data.part;

  return (
    <div className="space-y-4">
      <Link to={`/systems/${systemId}`} className="text-sm text-[#1e3a4f] hover:underline">
        ← Back to system
      </Link>
      <PageHeader
        title={String(part.part_name)}
        subtitle={`${String(part.part_number || "")} · ${data.system.name}`}
      />
      <div className="flex gap-6 text-sm">
        <span>
          On-hand: <strong className="tabular-nums">{data.onHand}</strong>
        </span>
        {part.description ? <span>{String(part.description)}</span> : null}
        {part.location_name ? <span>Location: {String(part.location_name)}</span> : null}
      </div>
      <h3 className="font-semibold text-[#1e3a4f]">Transaction History</h3>
      <DataTable
        columns={[
          { key: "date", header: "Date", render: (t) => new Date(String(t.created_at)).toLocaleString() },
          { key: "type", header: "Type", render: (t) => String(t.type_name) },
          {
            key: "qty",
            header: "Qty",
            className: "text-right tabular-nums",
            render: (t) => {
              const q = Number(t.quantity_change);
              return (
                <span className={q >= 0 ? "text-green-700" : "text-red-700"}>
                  {q >= 0 ? "+" : ""}{q}
                </span>
              );
            },
          },
          { key: "user", header: "User", render: (t) => String(t.user_name || "—") },
          { key: "notes", header: "Notes", render: (t) => String(t.notes || "") },
        ]}
        rows={data.transactions}
        rowKey={(t) => String(t.id)}
        emptyMessage="No transactions for this part."
      />
    </div>
  );
}
